/**
 * LibraryAgentDetail — Sandy-style agent editor.
 *
 * Layout:
 *   Header     — icon tile + name, Delete / Cancel / Save Draft buttons
 *   Tabs       — Configuration | Agent Group | Prompt (chars) | Agent Rules (count) | History (N)
 *
 *   Configuration tab (2 columns):
 *     LEFT  — Basic Information card: name, description, type, color,
 *             icon, enabled, protocol directory, specialties multi-tag
 *     RIGHT — Assigned Tools card: list of tools with + Add Tool picker
 *             Triggers card:      keyword pills with inline add
 *
 *   Prompt tab:
 *     Full-width prompt content editor (respects isLive read-only mode).
 *
 *   Protocols tab:
 *     Linked-protocols editor (shared ProtocolLinker component).
 *
 *   History tab:
 *     Version list with modal preview + restore-as-draft.
 *
 * Auto-save pattern: the header's Save button is a single "Save draft"
 * action that writes every field at once. This differs from the old
 * prompt-only save because Sandy's model has metadata + prompt as one
 * versioned doc — saving any field bumps the version.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import * as api from "../api.js";
import {
  PageHeader,
  Loading,
  ErrorBanner,
  Tabs,
  StatusBadge,
  VersionBadge,
  VersionHistory,
} from "../components.jsx";
import { useLibraryEnv, useEnvPrefix } from "../LibraryEnvContext.jsx";
import { AuthoredView, CompiledView } from "../components/prompt-builder.jsx";

const TYPE_OPTIONS = [
  { value: "sub_agent", label: "Sub-Agent" },
  // Orchestrators are no longer listed as agents — every group owns
  // exactly one, configured in the Agent Group detail page's
  // Orchestration tab. Existing orchestrator-type rows are filtered
  // out in LibraryAgents.
];

const DEFAULT_FORM = {
  name: "",
  description: "",
  agent_type: "sub_agent",
  color: "#455A64",
  icon: "",
  enabled: true,
  protocol_directory: "",
  specialties: [],
  customSpecialty: "",
  tools: [],
  triggers: [],
  newTrigger: "",
  linked_protocols: [],
  content: "",
};

function protocolKey(p) {
  return `${p.category}:${p.protocol_id}`;
}

export default function LibraryAgentDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { env, isLive } = useLibraryEnv();
  const prefix = useEnvPrefix();

  const [doc, setDoc] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("configuration");

  const [form, setForm] = useState(DEFAULT_FORM);
  const [original, setOriginal] = useState(DEFAULT_FORM);

  const [allTools, setAllTools] = useState([]);
  const [existingSpecialties, setExistingSpecialties] = useState([]);
  const [groupCatalog, setGroupCatalog] = useState([]);
  const [toolPickerOpen, setToolPickerOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [allProtocols, setAllProtocols] = useState([]);
  const [protocolsLoading, setProtocolsLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, histRes] = await Promise.all([
        api.getAgent(id, env),
        api.getAgentHistory(id).catch(() => ({ data: [] })),
      ]);
      setDoc(res.data);
      setHistory(histRes.data || []);
      setError(null);
      if (res.data) {
        const next = {
          ...DEFAULT_FORM,
          name: res.data.name || "",
          description: res.data.description || "",
          agent_type: res.data.agent_type || "sub_agent",
          color: res.data.color || "#455A64",
          icon: res.data.icon || "",
          enabled: res.data.enabled !== false,
          protocol_directory: res.data.protocol_directory || "",
          specialties: [...(res.data.specialties || [])],
          tools: [...(res.data.tools || [])],
          triggers: [...(res.data.triggers || [])],
          linked_protocols: [...(res.data.linked_protocols || [])],
          content: res.data.content || "",
        };
        setForm(next);
        setOriginal(next);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [id, env]);

  useEffect(() => {
    load();
  }, [load]);

  // Load the tools catalog + specialty catalog in the background so
  // the picker + specialty tagger have data to work with.
  useEffect(() => {
    (async () => {
      try {
        const [toolsRes, catRes, agentsRes] = await Promise.all([
          api.listTools().catch(() => ({ data: [] })),
          api.listSpecialtyCatalog().catch(() => ({ data: [] })),
          api.listAgents({ mode: "draft" }).catch(() => ({ data: [] })),
        ]);
        setAllTools(toolsRes.data || []);
        setGroupCatalog(catRes.data || []);
        const seen = new Set();
        for (const s of catRes.data || []) seen.add(s._id);
        for (const a of agentsRes.data || []) {
          for (const t of a.specialties || []) seen.add(t);
        }
        setExistingSpecialties([...seen].sort());
      } catch {
        // ignore
      }
    })();
  }, []);

  // Load protocols matching the agent's specialty tags so the protocols
  // tab can render the picker. Refetches when specialties change.
  useEffect(() => {
    const specialties = form.specialties || [];
    if (specialties.length === 0) {
      setAllProtocols([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setProtocolsLoading(true);
      try {
        // Fetch per specialty and merge — listProtocols supports one
        // specialty at a time.
        const results = await Promise.all(
          specialties.map((s) =>
            api.listProtocols({ specialty: s, mode: "draft" }).catch(() => ({ data: [] }))
          )
        );
        const merged = [];
        const seen = new Set();
        for (const r of results) {
          for (const p of r.data || []) {
            const k = protocolKey(p);
            if (!seen.has(k)) {
              seen.add(k);
              merged.push(p);
            }
          }
        }
        merged.sort((a, b) => {
          if (a.category !== b.category) return a.category.localeCompare(b.category);
          return a.protocol_id.localeCompare(b.protocol_id);
        });
        if (!cancelled) setAllProtocols(merged);
      } finally {
        if (!cancelled) setProtocolsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [form.specialties]);

  const dirty = useMemo(() => {
    const keys = [
      "name",
      "description",
      "agent_type",
      "color",
      "icon",
      "enabled",
      "protocol_directory",
      "content",
    ];
    for (const k of keys) if (form[k] !== original[k]) return true;
    if (!sameArray(form.specialties, original.specialties)) return true;
    if (!sameArray(form.tools, original.tools)) return true;
    if (!sameArray(form.triggers, original.triggers)) return true;
    if (!sameProtocolLinks(form.linked_protocols, original.linked_protocols))
      return true;
    return false;
  }, [form, original]);

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError(new Error("Name is required"));
      return;
    }
    if (form.specialties.length === 0) {
      setError(new Error("At least one specialty tag is required"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.writeAgent(id, {
        content: form.content,
        mode: "draft",
        name: form.name.trim(),
        description: form.description.trim(),
        agent_type: form.agent_type,
        color: form.color,
        icon: form.icon.trim() || null,
        enabled: form.enabled,
        protocol_directory: form.protocol_directory.trim() || null,
        specialties: form.specialties,
        tools: form.tools,
        triggers: form.triggers,
        linked_protocols: form.linked_protocols,
        base_version: doc?.version ?? null,
        author: "admin",
        commit_msg: `Edit agent ${id}`,
      });
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = () => {
    setForm(original);
    setError(null);
  };

  const handleRestore = async (version) => {
    if (
      !window.confirm(
        `Restore v${version} as a new draft? Creates a new draft version with the content of v${version}.`
      )
    )
      return;
    setBusy(true);
    try {
      await api.restoreAgentVersion(id, version, {
        mode: "draft",
        author: "admin",
        reason: `UI restore from v${version}`,
      });
      setSelectedVersion(null);
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  // ─── Tool management ─────────────────────────────────────────────
  const addTool = (toolId) => {
    if (!form.tools.includes(toolId)) {
      setForm((f) => ({ ...f, tools: [...f.tools, toolId] }));
    }
  };
  const removeTool = (toolId) => {
    setForm((f) => ({ ...f, tools: f.tools.filter((t) => t !== toolId) }));
  };

  // ─── Trigger management ──────────────────────────────────────────
  const addTrigger = () => {
    const t = (form.newTrigger || "").trim();
    if (!t) return;
    if (!form.triggers.includes(t)) {
      setForm((f) => ({ ...f, triggers: [...f.triggers, t], newTrigger: "" }));
    } else {
      setForm((f) => ({ ...f, newTrigger: "" }));
    }
  };
  const removeTrigger = (t) => {
    setForm((f) => ({ ...f, triggers: f.triggers.filter((x) => x !== t) }));
  };

  // ─── Specialty tag management ────────────────────────────────────
  const toggleSpecialty = (s) => {
    setForm((f) => ({
      ...f,
      specialties: f.specialties.includes(s)
        ? f.specialties.filter((x) => x !== s)
        : [...f.specialties, s],
    }));
  };
  const addCustomSpecialty = () => {
    const s = (form.customSpecialty || "").trim().toLowerCase();
    if (!s) return;
    if (!form.specialties.includes(s)) {
      setForm((f) => ({
        ...f,
        specialties: [...f.specialties, s],
        customSpecialty: "",
      }));
      if (!existingSpecialties.includes(s)) {
        setExistingSpecialties((arr) => [...arr, s].sort());
      }
    } else {
      setForm((f) => ({ ...f, customSpecialty: "" }));
    }
  };

  // ─── Render ──────────────────────────────────────────────────────
  const toolMap = useMemo(() => {
    const m = new Map();
    for (const t of allTools) m.set(t._id, t);
    return m;
  }, [allTools]);

  const promptChars = (form.content || "").length;
  const linkedProtocolCount = (form.linked_protocols || []).length;

  return (
    <>
      <PageHeader
        title={
          <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span
              className="agent-icon-tile"
              style={{ background: form.color || "#455A64" }}
            >
              {(form.icon && form.icon.trim()) ||
                (form.name || id).charAt(0).toUpperCase()}
            </span>
            {form.name || id}
          </span>
        }
        breadcrumbs={[
          { label: "Library", to: `${prefix}/platform` },
          { label: "Agents", to: `${prefix}/agents` },
          { label: doc?.name || id },
        ]}
        actions={
          <div className="btn-row">
            {doc && <VersionBadge version={doc.version} />}
            {doc && <StatusBadge doc={doc} />}
            {!isLive && (
              <>
                <button
                  className="btn"
                  onClick={handleCancel}
                  disabled={busy || !dirty}
                >
                  Cancel
                </button>
                <button
                  className="btn primary"
                  onClick={handleSave}
                  disabled={busy || !dirty}
                >
                  {busy ? "Saving..." : "Save draft"}
                </button>
              </>
            )}
          </div>
        }
      />

      <Tabs
        tabs={[
          { id: "configuration", label: "Configuration" },
          {
            id: "groups",
            label: `Agent Group (${(form.specialties || []).length})`,
          },
          {
            id: "prompt",
            label: `Prompt (${promptChars.toLocaleString()} chars)`,
          },
          {
            id: "protocols",
            label: `Agent Rules (${linkedProtocolCount})`,
          },
          { id: "history", label: `History (${history.length})` },
        ]}
        active={tab}
        onChange={setTab}
      />

      <div className="tab-content">
        <ErrorBanner error={error} />

        {loading ? (
          <Loading />
        ) : !doc ? (
          <div className="empty-state">
            <h3>Agent not found</h3>
            <p>
              <Link to={`${prefix}/agents`}>Back to agents</Link>
            </p>
          </div>
        ) : tab === "configuration" ? (
          <div className="agent-config-grid">
            <div>
              <BasicInformation
                form={form}
                setForm={setForm}
                existingSpecialties={existingSpecialties}
                toggleSpecialty={toggleSpecialty}
                addCustomSpecialty={addCustomSpecialty}
                readOnly={isLive}
              />
            </div>
            <div>
              <AssignedTools
                tools={form.tools}
                toolMap={toolMap}
                onRemove={removeTool}
                onOpenPicker={() => setToolPickerOpen(true)}
                readOnly={isLive}
              />
              <Triggers
                triggers={form.triggers}
                newTrigger={form.newTrigger}
                onChangeNew={(v) => setForm((f) => ({ ...f, newTrigger: v }))}
                onAdd={addTrigger}
                onRemove={removeTrigger}
                readOnly={isLive}
              />
            </div>
          </div>
        ) : tab === "groups" ? (
          <AgentGroupCard
            selected={form.specialties || []}
            onToggle={toggleSpecialty}
            catalog={groupCatalog}
            existing={existingSpecialties}
            customSpecialty={form.customSpecialty || ""}
            onChangeCustom={(v) =>
              setForm((f) => ({ ...f, customSpecialty: v }))
            }
            onAddCustom={addCustomSpecialty}
            readOnly={isLive}
          />
        ) : tab === "prompt" ? (
          <PromptTab agentId={id} env={env} />
        ) : tab === "protocols" ? (
          <LinkedProtocolsCard
            linked={form.linked_protocols || []}
            available={allProtocols}
            loading={protocolsLoading}
            specialties={form.specialties || []}
            onChange={(next) => updateField(["linked_protocols"], next)}
            readOnly={isLive}
            agentTools={form.tools || []}
            onAddTools={(ids) => {
              const next = Array.from(
                new Set([...(form.tools || []), ...ids])
              );
              updateField(["tools"], next);
            }}
          />
        ) : (
          <div className="card">
            <VersionHistory
              versions={history}
              onSelect={(v) => setSelectedVersion(v)}
            />
          </div>
        )}
      </div>

      {toolPickerOpen && (
        <ToolPicker
          allTools={allTools}
          selectedIds={form.tools}
          onAdd={addTool}
          onRemove={removeTool}
          onClose={() => setToolPickerOpen(false)}
        />
      )}

      {selectedVersion && (
        <VersionPreviewModal
          version={selectedVersion}
          onClose={() => setSelectedVersion(null)}
          onRestore={() => handleRestore(selectedVersion.version)}
          canRestore={!isLive && !selectedVersion.is_draft}
        />
      )}
    </>
  );
}

// ─── Linked Protocols card (Agent Rules tab) ──────────────────────
function LinkedProtocolsCard({
  linked,
  available,
  loading,
  specialties,
  onChange,
  readOnly,
  agentTools = [],
  onAddTools,
}) {
  const agentToolSet = useMemo(() => new Set(agentTools), [agentTools]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [linkState, setLinkState] = useState("all"); // all | linked | unlinked
  const [specialtyFilter, setSpecialtyFilter] = useState("all");

  const linkedSet = useMemo(() => {
    const s = new Set();
    for (const p of linked || []) s.add(protocolKey(p));
    return s;
  }, [linked]);

  // Per-rule missing-tools lint: for every LINKED rule in the pool,
  // compute which required_tools the current agent is missing.
  const missingByRule = useMemo(() => {
    const map = new Map();
    for (const p of available) {
      if (!linkedSet.has(protocolKey(p))) continue;
      const req = p.required_tools || [];
      const missing = req.filter((t) => !agentToolSet.has(t));
      if (missing.length > 0) map.set(protocolKey(p), missing);
    }
    return map;
  }, [available, linkedSet, agentToolSet]);

  const allMissingTools = useMemo(() => {
    const s = new Set();
    for (const missing of missingByRule.values()) {
      for (const t of missing) s.add(t);
    }
    return Array.from(s).sort();
  }, [missingByRule]);

  // Available categories + counts
  const categories = useMemo(() => {
    const counts = new Map();
    for (const p of available) {
      const c = p.category || "other";
      counts.set(c, (counts.get(c) || 0) + 1);
    }
    const list = Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([id, count]) => ({ id, count }));
    return [{ id: "all", count: available.length }, ...list];
  }, [available]);

  // Apply all filters before grouping
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return available.filter((p) => {
      if (category !== "all" && (p.category || "other") !== category) return false;
      if (specialtyFilter !== "all") {
        const tags = p.specialties || [];
        if (!tags.includes(specialtyFilter)) return false;
      }
      if (linkState === "linked" && !linkedSet.has(protocolKey(p))) return false;
      if (linkState === "unlinked" && linkedSet.has(protocolKey(p))) return false;
      if (q) {
        const hay = `${p.name || ""} ${p.protocol_id} ${p.description || ""} ${(p.body_parts || []).join(" ")}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [available, category, linkState, specialtyFilter, search, linkedSet]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const p of filtered) {
      const cat = p.category || "other";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(p);
    }
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  const toggleOne = (p) => {
    const k = protocolKey(p);
    if (linkedSet.has(k)) {
      onChange((linked || []).filter((x) => protocolKey(x) !== k));
    } else {
      onChange([...(linked || []), { category: p.category, protocol_id: p.protocol_id }]);
    }
  };

  const toggleCategory = (cat, checkAll) => {
    // Category bulk-toggle respects the current filtered list so
    // hidden rows are not accidentally flipped.
    const catProtocols = filtered.filter((p) => (p.category || "other") === cat);
    if (checkAll) {
      const additions = catProtocols
        .filter((p) => !linkedSet.has(protocolKey(p)))
        .map((p) => ({ category: p.category, protocol_id: p.protocol_id }));
      onChange([...(linked || []), ...additions]);
    } else {
      const remove = new Set(catProtocols.map(protocolKey));
      onChange((linked || []).filter((x) => !remove.has(protocolKey(x))));
    }
  };

  // Bulk actions operate on the current filter result, not the full
  // catalog — safer because hidden rows aren't touched.
  const selectVisible = () => {
    const next = new Map();
    for (const p of linked || []) next.set(protocolKey(p), p);
    for (const p of filtered) {
      next.set(protocolKey(p), { category: p.category, protocol_id: p.protocol_id });
    }
    onChange(Array.from(next.values()));
  };

  const clearVisible = () => {
    const remove = new Set(filtered.map(protocolKey));
    onChange((linked || []).filter((x) => !remove.has(protocolKey(x))));
  };

  const clearFilters = () => {
    setSearch("");
    setCategory("all");
    setLinkState("all");
    setSpecialtyFilter("all");
  };

  const filtersActive =
    search || category !== "all" || linkState !== "all" || specialtyFilter !== "all";

  if (specialties.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h3>Linked rules</h3>
        </div>
        <div className="empty-state">
          <h3>No specialty tags</h3>
          <p>
            Add at least one specialty in the Configuration tab. Protocols
            are filtered by the agent's specialty tags.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="card">
        <div className="card-header">
          <h3>Linked rules</h3>
        </div>
        <Loading label="Loading available protocols..." />
      </div>
    );
  }

  if (available.length === 0) {
    return (
      <div className="card">
        <div className="card-header">
          <h3>Linked rules</h3>
        </div>
        <div className="empty-state">
          <h3>No agent rules available</h3>
          <p>
            No protocols are tagged with {specialties.join(", ")}.{" "}
            <Link to="/library/draft/protocols/new">Create one</Link> or tag
            existing protocols with this specialty.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header" style={{ justifyContent: "space-between" }}>
        <h3>
          Linked rules{" "}
          <span className="muted small" style={{ fontWeight: 400, marginLeft: 6 }}>
            {linkedSet.size} of {available.length} linked
            {filtered.length !== available.length && (
              <> · {filtered.length} matching filter</>
            )}
          </span>
        </h3>
        {!readOnly && (
          <div className="btn-row">
            <button type="button" className="btn sm" onClick={selectVisible}>
              Select visible
            </button>
            <button type="button" className="btn sm" onClick={clearVisible}>
              Clear visible
            </button>
          </div>
        )}
      </div>
      <p className="muted small mb-16">
        Agent rules this agent can load at runtime via tool calls
        (e.g. <span className="mono">get_triage_protocol</span>,{" "}
        <span className="mono">get_appointment_instructions</span>). Available
        pool is filtered by the agent's specialty tags:{" "}
        <strong>{specialties.join(", ")}</strong>.
      </p>

      {/* Missing-tools lint — aggregated across every linked rule */}
      {allMissingTools.length > 0 && (
        <div className="missing-tools-banner">
          <div className="missing-tools-banner-left">
            <strong>
              {allMissingTools.length} tool
              {allMissingTools.length === 1 ? "" : "s"} missing
            </strong>
            <div className="muted small">
              Some linked rules declare tools this agent doesn't have.
              Agents must include these in their own tool list for the
              rules to work at runtime.
            </div>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 4,
                marginTop: 8,
              }}
            >
              {allMissingTools.map((t) => (
                <span key={t} className="badge badge-danger mono">
                  {t}
                </span>
              ))}
            </div>
          </div>
          {!readOnly && onAddTools && (
            <button
              type="button"
              className="btn sm primary"
              onClick={() => onAddTools(allMissingTools)}
            >
              + Add all to agent
            </button>
          )}
        </div>
      )}

      {/* Filter bar */}
      <div className="protocol-filter-bar">
        <div className="filter-row">
          <span className="muted small">Category:</span>
          <div className="btn-row" style={{ flexWrap: "wrap" }}>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`btn sm ${category === c.id ? "primary" : ""}`}
                onClick={() => setCategory(c.id)}
              >
                {c.id === "all" ? "All" : c.id}
                <span className="muted" style={{ marginLeft: 4 }}>
                  ({c.count})
                </span>
              </button>
            ))}
          </div>
        </div>

        <div className="filter-row">
          <span className="muted small">Show:</span>
          <div className="btn-row">
            {[
              { id: "all", label: "All", n: available.length },
              { id: "linked", label: "Linked only", n: linkedSet.size },
              {
                id: "unlinked",
                label: "Unlinked only",
                n: available.length - linkedSet.size,
              },
            ].map((o) => (
              <button
                key={o.id}
                type="button"
                className={`btn sm ${linkState === o.id ? "primary" : ""}`}
                onClick={() => setLinkState(o.id)}
              >
                {o.label}
                <span className="muted" style={{ marginLeft: 4 }}>
                  ({o.n})
                </span>
              </button>
            ))}
          </div>

          {specialties.length > 1 && (
            <>
              <span className="muted small" style={{ marginLeft: 12 }}>
                Specialty:
              </span>
              <div className="btn-row" style={{ flexWrap: "wrap" }}>
                <button
                  type="button"
                  className={`btn sm ${specialtyFilter === "all" ? "primary" : ""}`}
                  onClick={() => setSpecialtyFilter("all")}
                >
                  All
                </button>
                {specialties.map((s) => (
                  <button
                    key={s}
                    type="button"
                    className={`btn sm ${specialtyFilter === s ? "primary" : ""}`}
                    onClick={() => setSpecialtyFilter(s)}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </>
          )}

          <div className="spacer" />
          <input
            type="text"
            placeholder="Search name, id, description, body part..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              padding: "6px 12px",
              fontSize: 13,
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-sm)",
              minWidth: 260,
            }}
          />
          {filtersActive && (
            <button type="button" className="btn sm" onClick={clearFilters}>
              Clear filters
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 && (
        <div
          className="muted small"
          style={{ padding: "24px 12px", textAlign: "center" }}
        >
          No agent rules match the current filter.
        </div>
      )}

      {grouped.map(([cat, list]) => {
        const inCat = list.filter((p) => linkedSet.has(protocolKey(p)));
        const allInCat = inCat.length === list.length;
        const someInCat = inCat.length > 0 && !allInCat;
        return (
          <div
            key={cat}
            className="card"
            style={{
              padding: 0,
              marginBottom: 12,
              background: "var(--color-surface-muted)",
            }}
          >
            <div
              style={{
                padding: "10px 14px",
                borderBottom: "1px solid var(--color-border)",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              <input
                type="checkbox"
                checked={allInCat}
                ref={(el) => {
                  if (el) el.indeterminate = someInCat;
                }}
                onChange={(e) => toggleCategory(cat, e.target.checked)}
                disabled={readOnly}
              />
              <strong style={{ fontSize: 13 }}>{cat}</strong>
              <span className="muted small">
                {inCat.length} / {list.length}
              </span>
            </div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {list.map((p) => {
                const k = protocolKey(p);
                const on = linkedSet.has(k);
                const missing = on ? missingByRule.get(k) : null;
                const hasMissing = !!missing && missing.length > 0;
                return (
                  <li
                    key={k}
                    style={{
                      padding: "9px 14px 9px 36px",
                      borderBottom: "1px solid var(--color-border)",
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      background: on ? "#fff" : "transparent",
                      cursor: readOnly ? "default" : "pointer",
                    }}
                    onClick={() => !readOnly && toggleOne(p)}
                  >
                    <input
                      type="checkbox"
                      checked={on}
                      onChange={() => !readOnly && toggleOne(p)}
                      disabled={readOnly}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        {p.name || p.protocol_id}
                        {hasMissing && (
                          <span
                            className="badge badge-danger small"
                            title={`Missing tools: ${missing.join(", ")}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            ⚠ {missing.length} tool
                            {missing.length === 1 ? "" : "s"} missing
                          </span>
                        )}
                      </div>
                      {p.description && (
                        <div className="muted small" style={{ marginTop: 2 }}>
                          {p.description}
                        </div>
                      )}
                    </div>
                    <span className="mono small muted">
                      {(p.content?.length || 0).toLocaleString()} chars
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function sameArray(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

function sameProtocolLinks(a, b) {
  const aa = (a || []).slice().map(protocolKey).sort();
  const bb = (b || []).slice().map(protocolKey).sort();
  if (aa.length !== bb.length) return false;
  for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false;
  return true;
}

// ─── Basic Information card ─────────────────────────────────────────
function BasicInformation({
  form,
  setForm,
  existingSpecialties,
  toggleSpecialty,
  addCustomSpecialty,
  readOnly,
}) {
  return (
    <div className="card">
      <div className="card-header">
        <h3>Basic Information</h3>
      </div>

      <div className="form-field">
        <label>Name</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          readOnly={readOnly}
        />
      </div>

      <div className="form-field">
        <label>Description</label>
        <textarea
          rows={3}
          value={form.description}
          onChange={(e) =>
            setForm((f) => ({ ...f, description: e.target.value }))
          }
          readOnly={readOnly}
        />
      </div>

      <div className="field-row">
        <div className="form-field" style={{ flex: 1 }}>
          <label>Type</label>
          <select
            value={form.agent_type}
            onChange={(e) =>
              setForm((f) => ({ ...f, agent_type: e.target.value }))
            }
            disabled={readOnly}
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field" style={{ width: 70 }}>
          <label>Color</label>
          <input
            type="color"
            value={form.color}
            onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
            disabled={readOnly}
            style={{ height: 32, padding: 2 }}
          />
        </div>
        <div className="form-field" style={{ width: 70 }}>
          <label>Icon</label>
          <input
            type="text"
            maxLength={2}
            value={form.icon}
            onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
            readOnly={readOnly}
            style={{ textAlign: "center" }}
          />
        </div>
        <div className="form-field" style={{ width: 80 }}>
          <label>Enabled</label>
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) =>
              setForm((f) => ({ ...f, enabled: e.target.checked }))
            }
            disabled={readOnly}
            style={{ width: 20, height: 20 }}
          />
        </div>
      </div>

      <div className="form-field">
        <label>Protocol Directory</label>
        <input
          type="text"
          value={form.protocol_directory}
          onChange={(e) =>
            setForm((f) => ({ ...f, protocol_directory: e.target.value }))
          }
          readOnly={readOnly}
          placeholder="e.g. triage-protocols"
        />
        <div className="help">
          Directory of protocol files loaded at runtime via tool calls. Leave
          empty if not used.
        </div>
      </div>

      <div className="form-field">
        <div className="muted small">
          Group membership lives in the <strong>Agent Group</strong> tab
          above — a single agent can belong to one or more groups.
        </div>
      </div>
    </div>
  );
}

// ─── Agent Group tab — table of groups with checkbox toggles ──────
function AgentGroupCard({
  selected,
  onToggle,
  catalog,
  existing,
  customSpecialty,
  onChangeCustom,
  onAddCustom,
  readOnly,
}) {
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // Build the row list. Catalog entries come first (they have names +
  // descriptions + brand color), then any free-floating slug that's
  // used by an agent but isn't in the catalog shows up as a bare row.
  const rows = useMemo(() => {
    const map = new Map();
    for (const c of catalog || []) {
      map.set(c._id, {
        id: c._id,
        name: c.name || c._id,
        description: c.description || "",
        color: c.color || "#0f0f0f",
        category: c.category || null,
        source: "catalog",
      });
    }
    for (const slug of existing || []) {
      if (!map.has(slug)) {
        map.set(slug, {
          id: slug,
          name: slug,
          description: "",
          color: "#6b7280",
          category: null,
          source: "custom",
        });
      }
    }
    // Ensure selected groups always appear even if they're nowhere else
    for (const slug of selected || []) {
      if (!map.has(slug)) {
        map.set(slug, {
          id: slug,
          name: slug,
          description: "",
          color: "#6b7280",
          category: null,
          source: "custom",
        });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }, [catalog, existing, selected]);

  const [search, setSearch] = useState("");
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = `${r.name} ${r.id} ${r.description} ${r.category || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search]);

  return (
    <div className="card">
      <div className="card-header" style={{ justifyContent: "space-between" }}>
        <h3>
          Agent Group{" "}
          <span className="muted small" style={{ fontWeight: 400, marginLeft: 6 }}>
            {selectedSet.size} selected · {rows.length} available
          </span>
        </h3>
      </div>
      <p className="muted small mb-16">
        Groups bundle agents together so a tenant can enable a whole
        workflow at once. Check any group this agent should belong to — an
        agent can join multiple groups.
      </p>

      <div
        className="mb-16"
        style={{ display: "flex", gap: 10, alignItems: "center" }}
      >
        <input
          type="text"
          placeholder="Search groups..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: "8px 12px",
            fontSize: 13,
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-sm)",
            minWidth: 260,
          }}
        />
        <span className="muted small">
          {filtered.length} of {rows.length}
        </span>
      </div>

      <div
        className="card"
        style={{ padding: 0, marginBottom: readOnly ? 0 : 16 }}
      >
        <table className="table" style={{ border: "none" }}>
          <thead>
            <tr>
              <th style={{ width: 48 }}></th>
              <th style={{ width: "26%" }}>Group</th>
              <th>Description</th>
              <th style={{ width: 140 }}>Category</th>
              <th style={{ width: 90 }}>Source</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="muted small" style={{ padding: 16 }}>
                  No groups match your filter.
                </td>
              </tr>
            ) : (
              filtered.map((g) => {
                const on = selectedSet.has(g.id);
                return (
                  <tr
                    key={g.id}
                    className={readOnly ? "" : "clickable-row"}
                    onClick={() => !readOnly && onToggle(g.id)}
                    style={{ background: on ? "var(--color-surface-muted)" : undefined }}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => !readOnly && onToggle(g.id)}
                        disabled={readOnly}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        <span
                          className="agent-icon-tile"
                          style={{
                            width: 24,
                            height: 24,
                            fontSize: 11,
                            background: g.color,
                            borderRadius: 5,
                          }}
                        >
                          {(g.name || g.id).charAt(0).toUpperCase()}
                        </span>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>
                            {g.name}
                          </div>
                          <div className="muted small mono">{g.id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="muted small">
                      {g.description || <span className="subtle">—</span>}
                    </td>
                    <td>
                      {g.category ? (
                        <span className="badge badge-accent">{g.category}</span>
                      ) : (
                        <span className="subtle small">—</span>
                      )}
                    </td>
                    <td>
                      {g.source === "catalog" ? (
                        <span className="muted small">catalog</span>
                      ) : (
                        <span className="badge badge-neutral">custom</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <div className="btn-row">
          <input
            type="text"
            placeholder="Add a custom group slug..."
            value={customSpecialty}
            onChange={(e) => onChangeCustom(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onAddCustom();
              }
            }}
            style={{
              flex: 1,
              padding: "8px 12px",
              fontSize: 13,
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-sm)",
            }}
          />
          <button type="button" className="btn" onClick={onAddCustom}>
            Add
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Assigned Tools card ────────────────────────────────────────────
function AssignedTools({ tools, toolMap, onRemove, onOpenPicker, readOnly }) {
  return (
    <div className="card">
      <div className="card-header">
        <h3>
          Assigned Tools{" "}
          <span className="muted small" style={{ marginLeft: 6, fontWeight: 400 }}>
            {tools.length}
          </span>
        </h3>
      </div>
      <div className="agent-tool-list">
        {tools.length === 0 ? (
          <div className="muted small" style={{ padding: "12px 0" }}>
            No tools assigned. Click "+ Add Tool" to pick from the catalog.
          </div>
        ) : (
          tools.map((tid) => {
            const def = toolMap.get(tid);
            return (
              <div key={tid} className="agent-tool-row">
                <div className="agent-tool-info">
                  <div className="mono small" style={{ fontWeight: 600 }}>
                    {tid}
                  </div>
                  <div className="muted small">
                    {def?.description || (
                      <span className="subtle">
                        Not in catalog — remove or update tools list.
                      </span>
                    )}
                  </div>
                </div>
                {!readOnly && (
                  <button
                    type="button"
                    className="icon-btn"
                    onClick={() => onRemove(tid)}
                    title="Remove tool"
                  >
                    ×
                  </button>
                )}
              </div>
            );
          })
        )}
      </div>
      {!readOnly && (
        <div style={{ marginTop: 12 }}>
          <button type="button" className="btn sm" onClick={onOpenPicker}>
            + Add Tool
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Triggers card ──────────────────────────────────────────────────
function Triggers({
  triggers,
  newTrigger,
  onChangeNew,
  onAdd,
  onRemove,
  readOnly,
}) {
  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header">
        <h3>
          Triggers{" "}
          <span className="muted small" style={{ marginLeft: 6, fontWeight: 400 }}>
            {triggers.length}
          </span>
        </h3>
      </div>
      <p className="muted small mb-16">
        Keywords that route conversations to this agent.
      </p>
      <div className="trigger-pills">
        {triggers.length === 0 ? (
          <span className="subtle small">No triggers yet</span>
        ) : (
          triggers.map((t) => (
            <span key={t} className="trigger-pill">
              {t}
              {!readOnly && (
                <button
                  type="button"
                  className="trigger-pill-x"
                  onClick={() => onRemove(t)}
                >
                  ×
                </button>
              )}
            </span>
          ))
        )}
      </div>
      {!readOnly && (
        <div className="btn-row" style={{ marginTop: 12 }}>
          <input
            type="text"
            placeholder="Type a keyword and press Enter"
            value={newTrigger}
            onChange={(e) => onChangeNew(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onAdd();
              }
            }}
            style={{
              flex: 1,
              padding: "6px 10px",
              fontSize: 13,
              border: "1px solid var(--color-border)",
              borderRadius: "var(--radius-sm)",
            }}
          />
          <button type="button" className="btn sm" onClick={onAdd}>
            Add
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Prompt tab (full-width editor) ─────────────────────────────────
function PromptTab({ agentId, env }) {
  // Phase 3 (3-subtab rollout):
  //   • structured — sectioned authored view (the editable shape)
  //   • combined   — agent's contribution only, scope="agent" — universal
  //                  + framework rules excluded so reviewers can see what
  //                  the agent author actually wrote.
  //   • master     — full LLM view, scope="all" — universal + framework
  //                  + agent merged. This is byte-for-byte what the
  //                  runtime sends to Gemini.
  // The textarea editor is removed per design — operators edit via the
  // Structured view's controls (currently read-only render; inline edit
  // ships in the next round).
  const [subtab, setSubtab] = useState("structured");
  const [compiled, setCompiled] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // The library compiled-prompt endpoint is sub-tenant-scoped today.
  // For library-level preview we use the platform-default sub-tenant
  // pair if no specific scope is provided. Library agents are templates
  // — the compiler's variable-substitution falls back gracefully when
  // tenant variables aren't supplied.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    setCompiled(null);
    (async () => {
      try {
        // Try a representative sub-tenant for the preview. Library
        // agents are inheritance sources, so any forked sub-tenant
        // will produce a faithful render of the structured definition.
        const res = await fetch(
          `/api/tenants/metro-health/sub-tenants/metro-health-oncology/agents/${agentId}/compiled-prompt?direction=inbound`,
          { credentials: "include" }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const j = await res.json();
        if (!cancelled) setCompiled(j.data);
      } catch (e) {
        if (!cancelled) setErr(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [agentId, env]);

  return (
    <div className="card">
      <div className="card-header" style={{ justifyContent: "space-between" }}>
        <h3>Prompt</h3>
        <div className="btn-row">
          <button
            type="button"
            className={`btn sm ${subtab === "structured" ? "primary" : ""}`}
            onClick={() => setSubtab("structured")}
          >
            Structured editor
          </button>
          <button
            type="button"
            className={`btn sm ${subtab === "combined" ? "primary" : ""}`}
            onClick={() => setSubtab("combined")}
          >
            Combined (agent only)
          </button>
          <button
            type="button"
            className={`btn sm ${subtab === "master" ? "primary" : ""}`}
            onClick={() => setSubtab("master")}
          >
            Master Combined (LLM view)
          </button>
        </div>
      </div>

      {loading ? (
        <Loading label="Loading prompt builder..." />
      ) : err ? (
        <ErrorBanner error={err} />
      ) : !compiled ? (
        <div className="muted">No data yet.</div>
      ) : compiled.source === "prose" ? (
        <div style={{
          padding: 14,
          background: "#fff8e1",
          border: "1px solid #ffe0a3",
          borderRadius: "var(--radius-sm)",
          fontSize: 13, color: "#5b4400",
        }}>
          <strong>This agent uses the legacy prose prompt path.</strong>
          <div style={{ marginTop: 6 }}>{compiled.message}</div>
          {compiled.available_structured_agents?.length > 0 && (
            <div style={{ marginTop: 10, fontSize: 12 }}>
              Currently migrated to structured authoring:{" "}
              <strong>{compiled.available_structured_agents.join(", ")}</strong>
            </div>
          )}
        </div>
      ) : subtab === "structured" ? (
        <AuthoredView definition={compiled.definition} />
      ) : subtab === "combined" ? (
        <CompiledView
          prompt={compiled.prompt_agent_only}
          ruleMap={compiled.rule_map_agent_only}
          title="Agent contribution only — universal + framework rules excluded"
        />
      ) : (
        <CompiledView
          prompt={compiled.prompt}
          ruleMap={compiled.rule_map}
          title="Full prompt sent to LLM — universal + framework + agent merged"
        />
      )}
    </div>
  );
}

// ─── Tool picker modal ──────────────────────────────────────────────
function ToolPicker({ allTools, selectedIds, onAdd, onRemove, onClose }) {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");

  const categories = useMemo(() => {
    const seen = new Set();
    const list = [];
    for (const t of allTools) {
      const c = t.category || "Other";
      if (!seen.has(c)) {
        seen.add(c);
        list.push(c);
      }
    }
    return ["all", ...list.sort()];
  }, [allTools]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allTools.filter((t) => {
      if (category !== "all" && (t.category || "Other") !== category)
        return false;
      if (q) {
        const hay = `${t.name || t._id} ${t.description || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [allTools, category, search]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 720, width: "95%" }}
      >
        <h2>Add Tool</h2>
        <div className="muted small mb-16">
          Pick tools from the catalog. {allTools.length} total.
        </div>

        <div className="btn-row" style={{ flexWrap: "wrap", marginBottom: 8 }}>
          {categories.map((c) => (
            <button
              key={c}
              type="button"
              className={`btn sm ${category === c ? "primary" : ""}`}
              onClick={() => setCategory(c)}
            >
              {c === "all" ? "All" : c}
            </button>
          ))}
        </div>
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            width: "100%",
            padding: "8px 12px",
            fontSize: 13,
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-sm)",
            marginBottom: 12,
          }}
        />

        <div
          style={{
            maxHeight: "50vh",
            overflowY: "auto",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-sm)",
          }}
        >
          {filtered.length === 0 ? (
            <div className="muted small" style={{ padding: 16 }}>
              No tools match.
            </div>
          ) : (
            filtered.map((t) => {
              const selected = selectedIds.includes(t._id);
              return (
                <div
                  key={t._id}
                  className="tool-picker-row"
                  style={{
                    padding: "10px 14px",
                    borderBottom: "1px solid var(--color-border)",
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    background: selected ? "var(--color-success-subtle)" : "#fff",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      className="mono small"
                      style={{ fontWeight: 600, marginBottom: 2 }}
                    >
                      {t._id}
                    </div>
                    <div className="muted small">{t.description}</div>
                    <span
                      className="badge badge-neutral"
                      style={{ marginTop: 4 }}
                    >
                      {t.category || "Other"}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={`btn sm ${selected ? "" : "primary"}`}
                    onClick={() =>
                      selected ? onRemove(t._id) : onAdd(t._id)
                    }
                  >
                    {selected ? "✓ Added" : "+ Add"}
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div
          className="btn-row"
          style={{ marginTop: 16, justifyContent: "flex-end" }}
        >
          <button type="button" className="btn primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Version preview modal ──────────────────────────────────────────
function VersionPreviewModal({ version, onClose, onRestore, canRestore }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 900, width: "92%" }}
      >
        <h2>Version v{version.version}</h2>
        <div className="muted small mb-16">
          by {version.created_by || "unknown"} ·{" "}
          {version.commit_msg || <span className="subtle">no message</span>}
        </div>
        <pre
          style={{
            maxHeight: "55vh",
            overflow: "auto",
            padding: 12,
            margin: 0,
            background: "#fafafa",
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-sm)",
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            lineHeight: 1.55,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {version.content}
        </pre>
        <div
          className="btn-row"
          style={{ marginTop: 20, justifyContent: "flex-end" }}
        >
          <button className="btn" onClick={onClose}>
            Close
          </button>
          {canRestore && (
            <button className="btn primary" onClick={onRestore}>
              Restore as draft
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
